/**
 * Banner store
 * ------------
 * Single source of truth for banner priority. Only ONE banner is ever shown
 * at a time; when several are eligible the highest-priority one wins.
 *
 * Each banner owner reports its own *eligibility* (would-it-show, ignoring
 * priority) via `setEligibility`; the `visibleBanner` getter resolves the
 * winner from the ordered `BANNER_PRIORITY` list below. Consumers render iff
 * they are the winner (`isBannerVisible(type)`), so the ordering lives in
 * exactly one place instead of being encoded across component v-if chains and
 * cross-component boolean flags.
 *
 * Ownership of each type:
 *   PLAN_EXPIRED / DNS / ADDON_EXPIRED / AUTO_RENEWAL_WARNING / CONGRATS
 *   / ADD_MAILBOX_NUDGE_FIRST
 *     → AppBanner.vue (publishes from its `has*` computeds). The first-mailbox
 *       nudge lives here (not cpWidget) so it renders in the top banner slot
 *       alongside DNS and publishes synchronously — no async gate that would
 *       let DNS flash before it (MWDC-6350/6351).
 *   ADD_MAILBOX_NUDGE
 *     → cpWidget.vue (the "add more" content-area nudge; only mounted on the
 *       iframe homescreen, so it stays inactive elsewhere)
 */

import Vue from "vue";
import type { Module } from "vuex";

export const BANNER_TYPE = {
  PLAN_EXPIRED: "PLAN_EXPIRED",
  AUTO_RENEWAL_WARNING: "AUTO_RENEWAL_WARNING",
  // First-mailbox nudge — sits above DNS (spec §7 priority 4): a brand-new user
  // with no mailbox is nudged to create one before domain warnings.
  ADD_MAILBOX_NUDGE_FIRST: "ADD_MAILBOX_NUDGE_FIRST",
  DNS: "DNS",
  ADDON_EXPIRED: "ADDON_EXPIRED",
  CONGRATS: "CONGRATS",
  // Subsequent ("add more") nudge — lowest (spec §7 priority 7): loses to every
  // billing/DNS banner and to Congratulations.
  ADD_MAILBOX_NUDGE: "ADD_MAILBOX_NUDGE",
} as const;

/** One of the known banner identifiers. */
export type TBannerType = (typeof BANNER_TYPE)[keyof typeof BANNER_TYPE];

/**
 * Banner priority, highest → lowest — mirrors spec §7's "Banner priority on
 * Landing Home Screen" table (Plan Renewal Warning, priority 2, is built but
 * currently off, so it's omitted here):
 *   1 Plan / Add-on Expired      → PLAN_EXPIRED
 *   3 Auto-Renewal Warning       → AUTO_RENEWAL_WARNING
 *   4 First email account banner → ADD_MAILBOX_NUDGE_FIRST
 *   5 DNS / Domain Issues        → DNS
 *   6 Add-on Expiry              → ADDON_EXPIRED
 *   6 Congratulations            → CONGRATS
 *   7 Subsequent email banner    → ADD_MAILBOX_NUDGE
 */
export const BANNER_PRIORITY: TBannerType[] = [
  BANNER_TYPE.PLAN_EXPIRED,
  BANNER_TYPE.AUTO_RENEWAL_WARNING,
  BANNER_TYPE.ADD_MAILBOX_NUDGE_FIRST,
  BANNER_TYPE.DNS,
  BANNER_TYPE.ADDON_EXPIRED,
  BANNER_TYPE.CONGRATS,
  BANNER_TYPE.ADD_MAILBOX_NUDGE,
];

export interface TBannerState {
  /** Map of banner type → eligible (would show if priority allowed). */
  eligibility: Partial<Record<TBannerType, boolean>>;
}

const getDefaultState = (): TBannerState => ({
  eligibility: {},
});

// Root state is untyped (rest of the store is JS), so use `any` for the
// root generic — matches the pattern in mailbox-nudge.store.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bannerModule: Module<TBannerState, any> = {
  namespaced: true,
  state: getDefaultState,
  mutations: {
    setEligibility(state, { type, eligible }: { type: TBannerType; eligible: boolean }) {
      // Vue.set so newly-seen keys stay reactive.
      Vue.set(state.eligibility, type, !!eligible);
    },
    resetState(state) {
      Object.assign(state, getDefaultState());
    },
  },
  actions: {
    setEligibility({ commit }, payload: { type: TBannerType; eligible: boolean }) {
      commit("setEligibility", payload);
    },
    resetState({ commit }) {
      commit("resetState");
    },
  },
  getters: {
    priority: () => BANNER_PRIORITY,
    /** Highest-priority eligible banner, or null when none are eligible. */
    visibleBanner: (state): TBannerType | null =>
      BANNER_PRIORITY.find(type => state.eligibility[type]) || null,
    isBannerVisible:
      (_state, getters) =>
      (type: TBannerType): boolean =>
        getters.visibleBanner === type,
  },
};

export default bannerModule;
